/**
 * Peak / trough detection for every price and index graph on the site.
 *
 * A single unbroken line tells a reader nothing about how a card actually got
 * to today's number, so every chart marks the turning points — each local high
 * and each local low, plus the first and last day — as hoverable price points.
 * Flat stretches collapse to no marker, so a card with no sales history still
 * renders as one clean line instead of fake noise.
 */
export interface Extremum {
  index: number;
  kind: "peak" | "trough" | "start" | "end";
}

/**
 * Indexes worth marking on a series, in chart order.
 *
 * `tolerance` is the fraction of the series range a move must clear before it
 * counts as a turn, which keeps rounding jitter from producing a dotted mess.
 */
export function extrema(values: number[], tolerance = 0.02): Extremum[] {
  const n = values.length;
  if (n === 0) return [];
  if (n < 3) return values.map((_, i) => ({ index: i, kind: i === 0 ? "start" : "end" }) as Extremum);

  const min = Math.min(...values);
  const max = Math.max(...values);
  const band = (max - min) * tolerance;

  const out: Extremum[] = [{ index: 0, kind: "start" }];
  for (let i = 1; i < n - 1; i += 1) {
    const v = values[i];
    // Walk out to the nearest differing neighbours so plateaus are handled.
    let l = i - 1;
    while (l > 0 && Math.abs(values[l] - v) <= band) l -= 1;
    let r = i + 1;
    while (r < n - 1 && Math.abs(values[r] - v) <= band) r += 1;
    const left = values[l];
    const right = values[r];
    if (v - left > band && v - right > band) out.push({ index: i, kind: "peak" });
    else if (left - v > band && right - v > band) out.push({ index: i, kind: "trough" });
  }
  out.push({ index: n - 1, kind: "end" });

  // De-duplicate and keep chart order.
  const seen = new Set<number>();
  return out
    .sort((a, b) => a.index - b.index)
    .filter((e) => (seen.has(e.index) ? false : (seen.add(e.index), true)));
}

/** True when a series is genuinely flat (no sales history to plot). */
export function isFlatSeries(values: number[]): boolean {
  if (values.length < 2) return true;
  const min = Math.min(...values);
  const max = Math.max(...values);
  return max - min < Math.max(0.01, min * 0.0005);
}
